/**
 * Route guards. Separate from the router so that file exports only the route table —
 * mixing components and non-components in one module breaks fast refresh.
 */
import type { JSX, ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import type { Role } from "@/api/types";
import { landingFor } from "@/app/landing";
import { Button, Card, PageLoader } from "@/components/ui";
import { AgencyChooser } from "@/features/auth/AgencyChooser";
import { LoginPage } from "@/features/auth/LoginPage";
import { useTranslation } from "@/i18n";
import { useAuthStore, type AuthUser } from "@/store/auth";

/**
 * The paths a platform admin with no agency may open: the panel and the kit. Everything else
 * under the agency shell is an agency's screen.
 */
const PANEL_PATHS: readonly string[] = ["/platforma", "/ui-kit"];

/**
 * Where a signed-in user with no agency is sent from the agency shell, or `null` to stay.
 * A platform admin with no agency belongs in the panel from any tenant route (D202); a user
 * who is not an admin is refused by the route gates, not here.
 */
export function agencyRedirectFor(user: AuthUser | null, pathname: string): string | null {
  if (user === null || user.tenant !== null || !user.is_platform_admin) {
    return null;
  }
  const onPanel = PANEL_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  return onPanel ? null : "/platforma";
}

/** Wraps the agency shell: a platform admin with no agency never reaches an agency screen. */
export function RequireAgency({ children }: { children: ReactNode }): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const target = agencyRedirectFor(user, location.pathname);
  if (target !== null) {
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
}

/**
 * Gate for the authenticated area.
 *
 * `status` starts as "unknown" while a stored token is exchanged for the current user;
 * redirecting during that window would bounce a signed-in person back to the login
 * page on every refresh.
 */
export function RequireAuth(): JSX.Element {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === "unknown") {
    return <PageLoader />;
  }
  if (status === "unreachable") {
    return <ServerUnreachable />;
  }
  if (status === "anonymous") {
    return <Navigate to="/kirish" replace state={{ from: location.pathname }} />;
  }
  if (status === "choose-agency") {
    return <AgencyChooser />;
  }
  return <Outlet />;
}

/**
 * What a person sees when the API could not be asked at all. The loader is never left
 * spinning on a request that has already failed: a deploy restarting the API answers 503
 * without CORS headers, the browser reports that as a network error, and the session it
 * was checking is unknown rather than over.
 */
export function ServerUnreachable(): JSX.Element {
  const { t } = useTranslation();
  const restore = useAuthStore((state) => state.restore);
  return (
    <div className="k-unreachable" role="alert">
      <Card className="k-state">
        <p className="k-state__error">{t("auth.unreachable")}</p>
        <Button onClick={() => void restore()}>{t("common.retry")}</Button>
      </Card>
    </div>
  );
}

/**
 * Gate for a screen only some roles may open.
 *
 * It sits in the router, beside `RequireAuth`, rather than inside each screen. A check
 * written into a component is a check the next screen forgets to copy — which is how
 * `/sozlamalar` shipped with no gate at all while `/hisobotlar` had one. One guard, one
 * place, and a new protected route is an `allow` prop rather than a new convention.
 *
 * `allow` is a predicate from `lib/permissions.ts`, the same module the screens ask
 * before showing a control, so the router and the controls cannot disagree.
 *
 * The API is still the authority: this only replaces a screen that would fill with
 * permission errors with a sentence saying so. A signed-in user with no agency role is
 * refused here too, and a platform admin with no agency is sent to the panel.
 */
export function RequireRole({ allow }: { allow: (role: Role) => boolean }): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const role = user?.role ?? undefined;

  if (role === undefined) {
    // No agency role. A platform admin belongs in the panel; anyone else has no screen here.
    if (user?.is_platform_admin === true) {
      return <Navigate to="/platforma" replace />;
    }
    return <Card className="k-noAccess">{t("common.noAccess")}</Card>;
  }
  if (!allow(role)) {
    return <Card className="k-noAccess">{t("common.noAccess")}</Card>;
  }
  return <Outlet />;
}

/**
 * Gate for screens only Kapitan staff may open, such as the ui-kit page.
 *
 * Reads the flag the API returns on `/api/auth/me/`. Like `RequireRole`, it is a
 * convenience for the person, not a security boundary: any platform endpoint refuses
 * a non-admin on its own.
 */
export function RequirePlatformAdmin(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  // Fails closed: without a resolved user there is nothing to prove the flag on.
  if (user === null || !user.is_platform_admin) {
    return <Card className="k-noAccess">{t("auth.platformOnly")}</Card>;
  }
  return <Outlet />;
}

/**
 * Gate for a screen an agency role and Kapitan staff may both open — the provider catalogue
 * mapping and the operator connections, which the owner keeps and support fixes. A platform
 * admin passes whatever their role; anyone else is judged by `allow`. A platform admin with no
 * agency never gets here: `RequireAgency` sends them to the panel first.
 */
export function RequireRoleOrPlatformAdmin({ allow }: { allow: (role: Role) => boolean }): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  if (user?.is_platform_admin === true) {
    return <Outlet />;
  }
  const role = user?.role ?? undefined;
  if (role === undefined || !allow(role)) {
    return <Card className="k-noAccess">{t("common.noAccess")}</Card>;
  }
  return <Outlet />;
}

export function LoginRoute(): JSX.Element {
  const status = useAuthStore((state) => state.status);
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  if (status === "authenticated") {
    return <Navigate to={landingFor(role)} replace />;
  }
  if (status === "choose-agency") {
    return <AgencyChooser />;
  }
  if (status === "unreachable") {
    return <ServerUnreachable />;
  }
  return <LoginPage />;
}
